import React, {useEffect, useState} from "react";
import Header from "../../components/header/header";
import styles from "./pricing.module.css";
import axiosInstance from "../../AxiosInstance";
import {useTranslation} from "react-i18next";
import PricingCard from "./components/PricingCard";
import PaymentModal from "./components/PaymentModal";
import ConfirmationModal from "./components/ConfirmationModal";
import FreePlanSuccessModal from "./components/FreePlanSuccessModal";
import BillingRequiredModal from "./components/BillingRequiredModal";

export default function Pricing() {
    const {t} = useTranslation();
    const [loading, setLoading] = useState(true);
    const [plans, setPlans] = useState([]);
    const [currentSubscription, setCurrentSubscription] = useState(null);
    const [bankInfo, setBankInfo] = useState(null);

    const [selectedPlan, setSelectedPlan] = useState(null);
    const [showPaymentModal, setShowPaymentModal] = useState(false);
    const [showConfirmationModal, setShowConfirmationModal] = useState(false);
    const [confirmationData, setConfirmationData] = useState(null);

    const [requesting, setRequesting] = useState(false);
    const [error, setError] = useState(null);
    const [checkingBilling, setCheckingBilling] = useState(false);

    const [showFreePlanSuccess, setShowFreePlanSuccess] = useState(false);
    const [freePlanMessage, setFreePlanMessage] = useState(null);
    const [activatedPlanName, setActivatedPlanName] = useState(null);

    const [showBillingRequired, setShowBillingRequired] = useState(false);
    const [missingBillingFields, setMissingBillingFields] = useState([]);

    const [billingCycle, setBillingCycle] = useState('monthly'); // 'monthly' or 'yearly'
    const [billingCountry, setBillingCountry] = useState(null);

    // Helper function to check if country is Peru (IGV applies)
    const isPeru = (countryValue) => {
        if (!countryValue) return false;
        const value = countryValue.toUpperCase().trim();
        return ['PE', 'PERU', 'PERÚ'].includes(value);
    };

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        setLoading(true);
        try {
            // Fetch plans (required)
            const plansRes = await axiosInstance.get('subscription_request/plans/');
            setPlans(plansRes.data || []);

            // Fetch current subscription (optional - may not exist)
            try {
                const subscriptionRes = await axiosInstance.get('subscription_request/current/');
                setCurrentSubscription(subscriptionRes.data?.subscription || null);
            } catch (subErr) {
                console.log('No current subscription:', subErr);
                setCurrentSubscription(null);
            }

            // Fetch bank info (required for payment)
            try {
                const bankRes = await axiosInstance.get('subscription_request/bank_info/');
                console.log('Bank info response:', bankRes.data);
                setBankInfo(bankRes.data || null);
            } catch (bankErr) {
                console.error('Error fetching bank info:', bankErr);
                setBankInfo(null);
            }

            // Fetch billing info to get country for IGV calculation
            try {
                const billingRes = await axiosInstance.get('billing/overview/');
                setBillingCountry(billingRes.data?.billing_info?.country || null);
            } catch (billingErr) {
                console.error('Error fetching billing info:', billingErr);
                setBillingCountry(null);
            }
        } catch (err) {
            console.error('Error fetching pricing data:', err);
            setError('Error loading plans');
        } finally {
            setLoading(false);
        }
    };

    const checkBillingComplete = async () => {
        try {
            const response = await axiosInstance.get('billing/check_billing_complete/');
            return response.data;
        } catch (err) {
            console.error('Error checking billing:', err);
            return { is_complete: false, missing_fields: [] };
        }
    };

    const calculatePricing = (plan) => {
        const taxRate = isPeru(billingCountry) ? 0.18 : 0;
        const showIGV = taxRate > 0;

        if (plan.is_free || plan.price === 0) {
            return {
                monthlyPrice: 0,
                finalPrice: 0,
                finalPriceWithTax: 0,
                tax: 0,
                taxRate,
                showIGV,
                savings: 0,
                discountApplied: null,
                period: billingCycle === 'yearly' ? '12 months' : '1 month'
            };
        }

        // Use plan's annual_discount_percent if available, otherwise default to 10%
        const annualDiscountPercent = plan.annual_discount_percent || 10;
        const discountMultiplier = 1 - (annualDiscountPercent / 100);

        if (billingCycle === 'yearly') {
            const yearlyPrice = plan.price * 12 * discountMultiplier;
            const tax = Math.round(yearlyPrice * taxRate * 100) / 100;
            const yearlyPriceWithTax = Math.round((yearlyPrice + tax) * 100) / 100;
            return {
                monthlyPrice: plan.price,
                finalPrice: yearlyPrice,
                finalPriceWithTax: yearlyPriceWithTax,
                tax,
                taxRate,
                showIGV,
                savings: plan.price * 12 * (annualDiscountPercent / 100),
                discountApplied: `${annualDiscountPercent}%`,
                period: '12 months'
            };
        }

        const tax = Math.round(plan.price * taxRate * 100) / 100;
        const finalPriceWithTax = Math.round((plan.price + tax) * 100) / 100;
        return {
            monthlyPrice: plan.price,
            finalPrice: plan.price,
            finalPriceWithTax,
            tax,
            taxRate,
            showIGV,
            savings: 0,
            discountApplied: null,
            period: '1 month'
        };
    };

    const handleSelectPlan = async (plan) => {
        setError(null);

        // For paid plans, verify billing info first
        if (!plan.is_free && plan.price > 0) {
            setCheckingBilling(true);
            const billingCheck = await checkBillingComplete();
            setCheckingBilling(false);

            if (!billingCheck.is_complete) {
                setMissingBillingFields(billingCheck.missing_fields || []);
                setShowBillingRequired(true);
                return;
            }
        }

        // Check if it's a free plan - activate directly without payment modal
        if (plan.is_free || plan.price === 0) {
            setRequesting(true);
            try {
                const response = await axiosInstance.post('subscription_request/request_plan/', {
                    payment_plan_id: plan.id
                });

                if (response.data.is_free) {
                    // Free plan activated successfully - show success modal
                    setFreePlanMessage(response.data.message);
                    setActivatedPlanName(plan.name);
                    setShowFreePlanSuccess(true);

                    // Refresh subscription data
                    const subscriptionRes = await axiosInstance.get('subscription_request/current/');
                    setCurrentSubscription(subscriptionRes.data?.subscription || null);
                } else {
                    // Fallback - show confirmation modal with any data returned
                    setConfirmationData(response.data);
                    setShowConfirmationModal(true);
                }
            } catch (err) {
                console.error('Error activating free plan:', err);
                handleRequestError(err, 'Error activating free plan');
            } finally {
                setRequesting(false);
            }
        } else {
            // Paid plan - show payment modal
            setSelectedPlan(plan);
            setShowPaymentModal(true);
        }
    };

    const handleRequestError = (err, defaultMessage) => {
        const errorCode = err.response?.data?.error_code;

        // Handle billing-related errors
        if (errorCode === 'BILLING_INFO_REQUIRED' || errorCode === 'BILLING_INFO_INCOMPLETE') {
            setMissingBillingFields(err.response?.data?.missing_fields || []);
            setShowBillingRequired(true);
            return;
        }

        if (err.response?.data?.error) {
            setError(err.response.data.error);
        } else if (err.response?.data?.message) {
            setError(err.response.data.message);
        } else {
            setError(defaultMessage);
        }
    };

    const handleConfirmPurchase = async () => {
        if (!selectedPlan) return;

        setRequesting(true);
        setError(null);

        try {
            const response = await axiosInstance.post('subscription_request/request_plan/', {
                payment_plan_id: selectedPlan.id,
                billing_cycle: billingCycle
            });

            setConfirmationData(response.data);
            setShowPaymentModal(false);
            setShowConfirmationModal(true);

            // Refresh subscription data
            const subscriptionRes = await axiosInstance.get('subscription_request/current/');
            setCurrentSubscription(subscriptionRes.data?.subscription || null);
        } catch (err) {
            console.error('Error requesting plan:', err);
            const errorCode = err.response?.data?.error_code;

            // Handle billing-related errors - close payment modal and show billing modal
            if (errorCode === 'BILLING_INFO_REQUIRED' || errorCode === 'BILLING_INFO_INCOMPLETE') {
                setShowPaymentModal(false);
                setMissingBillingFields(err.response?.data?.missing_fields || []);
                setShowBillingRequired(true);
            } else {
                handleRequestError(err, 'Error processing request');
            }
        } finally {
            setRequesting(false);
        }
    };

    const closePaymentModal = () => {
        setShowPaymentModal(false);
        setSelectedPlan(null);
        setError(null);
    };

    const closeConfirmationModal = () => {
        setShowConfirmationModal(false);
        setConfirmationData(null);
        setSelectedPlan(null);
    };

    const getStatusBadge = (status) => {
        const statusMap = {
            'pending_payment': { labelKey: 'pricing.status.pending', color: '#F59E0B', bg: '#FEF3C7' },
            'active': { labelKey: 'pricing.status.active', color: '#10B981', bg: '#D1FAE5' },
            'trialing': { labelKey: 'pricing.status.trialing', color: '#3B82F6', bg: '#DBEAFE' },
            'past_due': { labelKey: 'pricing.status.pastDue', color: '#F59E0B', bg: '#FEF3C7' },
            'cancelled': { labelKey: 'pricing.status.cancelled', color: '#EF4444', bg: '#FEE2E2' }
        };
        const statusInfo = statusMap[status] || { labelKey: null, color: '#64748B', bg: '#F1F5F9' };
        return (
            <span style={{
                backgroundColor: statusInfo.bg,
                color: statusInfo.color,
                padding: '6px 16px',
                borderRadius: '20px',
                fontSize: '14px',
                fontWeight: '600'
            }}>
                {statusInfo.labelKey ? t(statusInfo.labelKey) : status}
            </span>
        );
    };

    if (loading) {
        return (
            <div className={styles.container}>
                <Header/>
                <div className={styles.innerContainer}>
                    <div className={styles.loadingContainer}>
                        <div>{t('pricing.loadingPlans')}</div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={styles.container}>
            <Header/>

            <PaymentModal
                show={showPaymentModal}
                plan={selectedPlan}
                bankInfo={bankInfo}
                onClose={closePaymentModal}
                onConfirm={handleConfirmPurchase}
                requesting={requesting}
                error={error}
                billingCycle={billingCycle}
                pricing={selectedPlan ? calculatePricing(selectedPlan) : null}
            />

            <ConfirmationModal
                show={showConfirmationModal}
                data={confirmationData}
                onClose={closeConfirmationModal}
            />

            <FreePlanSuccessModal
                show={showFreePlanSuccess}
                message={freePlanMessage}
                planName={activatedPlanName}
                onClose={() => {
                    setShowFreePlanSuccess(false);
                    setFreePlanMessage(null);
                    setActivatedPlanName(null);
                }}
            />

            <BillingRequiredModal
                show={showBillingRequired}
                missingFields={missingBillingFields}
                onClose={() => {
                    setShowBillingRequired(false);
                    setMissingBillingFields([]);
                }}
            />

            <div className={styles.innerContainer}>
                <div className={styles.header}>
                    <h1 className={styles.title}>{t('pricing.plansAndPricing')}</h1>
                    <p className={styles.subtitle}>
                        {t('pricing.choosePlan')}
                    </p>
                </div>

                {currentSubscription && (
                    <div className={styles.currentPlanBanner}>
                        <div className={styles.currentPlanInfo}>
                            <span>{t('pricing.currentPlanBanner')} </span>
                            <strong>{currentSubscription.payment_plan?.name || t('pricing.noPlan')}</strong>
                        </div>
                        <div>{getStatusBadge(currentSubscription.status)}</div>
                    </div>
                )}

                {/* Billing Cycle Toggle */}
                <div className={styles.billingToggle}>
                    <button
                        className={`${styles.toggleButton} ${billingCycle === 'monthly' ? styles.toggleActive : ''}`}
                        onClick={() => setBillingCycle('monthly')}
                    >
                        {t('pricing.monthly')}
                    </button>
                    <button
                        className={`${styles.toggleButton} ${billingCycle === 'yearly' ? styles.toggleActive : ''}`}
                        onClick={() => setBillingCycle('yearly')}
                    >
                        {t('pricing.yearly')}
                        {(() => {
                            const maxDiscount = Math.max(...plans
                                .filter(p => !p.is_free && p.price > 0)
                                .map(p => p.annual_discount_percent || 10));
                            return maxDiscount > 0 ? (
                                <span className={styles.discountBadge}>{t('pricing.saveUpTo', {percent: maxDiscount})}</span>
                            ) : null;
                        })()}
                    </button>
                </div>

                {checkingBilling && (
                    <div className={styles.checkingBillingOverlay}>
                        <div className={styles.checkingBillingMessage}>
                            {t('pricing.verifyingBilling')}
                        </div>
                    </div>
                )}

                <div className={styles.plansGrid}>
                    {plans.sort((a, b) => a.order - b.order).map((plan) => (
                        <PricingCard
                            key={plan.id}
                            plan={plan}
                            currentSubscription={currentSubscription}
                            onSelect={handleSelectPlan}
                            disabled={checkingBilling || requesting}
                            billingCycle={billingCycle}
                            pricing={calculatePricing(plan)}
                        />
                    ))}
                </div>

                <div className={styles.footer}>
                    <p>{t('pricing.customPlan')} <a href="mailto:info@pritech.com.pe">{t('pricing.contactUs')}</a></p>
                </div>
            </div>
        </div>
    );
}
